(() => {
  const configNode = document.getElementById('pilenium-upsell-script');
  const preferredProductUrls = [configNode?.dataset.openUpUrl, configNode?.dataset.macaronUrl].filter(Boolean);
  const caseProductUrl = configNode?.dataset.caseUrl || '';
  const labels = {
    save: configNode?.dataset.save || 'Save',
    add: configNode?.dataset.add || 'Add',
    added: configNode?.dataset.added || '✓ Added',
    addToCart: configNode?.dataset.addToCart || 'Add to cart',
  };
  let busy = false;

  function localeRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return `/${String(root).replace(/^\/+|\/+$/g, '')}${root === '/' ? '' : '/'}`;
  }

  function endpoint(path) {
    return `${localeRoot()}${String(path).replace(/^\/+/, '')}`;
  }

  function localizeProductUrl(url) {
    const value = String(url || '#');
    const root = localeRoot();
    if (root !== '/' && value.startsWith('/products/')) return `${root.slice(0, -1)}${value}`;
    return value;
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  function escapeHtml(value) {
    const node = document.createElement('div');
    node.textContent = value || '';
    return node.innerHTML;
  }

  function firstVariant(product) {
    return (product.variants || []).find((variant) => variant.available) || (product.variants || [])[0];
  }

  function isDiscounted(variant) {
    return Boolean(variant && variant.compare_at_price && variant.compare_at_price > variant.price);
  }

  function promotionRank(product) {
    return isDiscounted(firstVariant(product)) ? 1 : 0;
  }

  function imageUrl(image, width = 150) {
    const source = typeof image === 'string' ? image : image?.src || '';
    if (!source) return '';
    return `${source}${source.includes('?') ? '&' : '?'}width=${width}`;
  }

  function fetchProduct(url) {
    const cleanUrl = String(url || '').split('?')[0].replace(/\.js$/, '');
    if (!cleanUrl) return Promise.resolve(null);
    return fetch(`${cleanUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (product) product.pilenium_url = cleanUrl;
        return product;
      })
      .catch(() => null);
  }

  function productCard(product) {
    const variant = firstVariant(product);
    if (!variant) return '';
    const discounted = isDiscounted(variant);
    const discount = discounted ? Math.round(100 * (1 - variant.price / variant.compare_at_price)) : 0;
    const title = (product.title || '').split(' 20')[0].split(' - ')[0].split(' – ')[0];
    const safeTitle = escapeHtml(title);
    const productUrl = escapeHtml(product.pilenium_url || localizeProductUrl(product.url));
    const featured = imageUrl(product.featured_image || product.images?.[0]);
    const price = discounted
      ? `<span class="pil-upsell-card__p pil-upsell-card__p--promo"><s>${money(variant.compare_at_price)}</s> ${money(variant.price)}</span>`
      : `<span class="pil-upsell-card__p">${money(variant.price)}</span>`;
    const badge = discounted
      ? `<span class="pil-upsell-promo">${escapeHtml(labels.save)} −${discount}%</span>`
      : '';
    const image = featured
      ? `<img class="pil-upsell-card__img" src="${escapeHtml(featured)}" alt="" loading="lazy" width="150" height="150">`
      : '';

    return `<div class="pil-upsell-card${discounted ? ' pil-upsell-card--promo' : ''}">
      ${badge}
      <a class="pil-upsell-card__media" href="${productUrl}" tabindex="-1" aria-hidden="true">${image}</a>
      <a class="pil-upsell-card__b" href="${productUrl}"><span class="pil-upsell-card__n">${safeTitle}</span>${price}</a>
      <button type="button" class="pil-upsell-add${discounted ? ' pil-upsell-add--promo' : ''}" data-pil-add="${variant.id}" aria-label="${escapeHtml(labels.addToCart)} — ${safeTitle}">
        <span class="pil-upsell-add__txt">＋ ${escapeHtml(labels.add)}</span>
        <span class="pil-upsell-add__ok" aria-hidden="true">${escapeHtml(labels.added)}</span>
      </button>
    </div>`;
  }

  function estimatedBottleCount(cart) {
    return (cart.items || []).reduce((count, item) => {
      const title = String(item.title || '');
      let units = 1;
      if (/(?:^|\D)12(?:\D|$)/.test(title)) units = 12;
      else if (/(?:^|\D)6(?:\D|$)/.test(title)) units = 6;
      else if (/(?:^|\D)3(?:\D|$)/.test(title)) units = 3;
      return count + units * item.quantity;
    }, 0);
  }

  function loadUpsells() {
    const row = document.getElementById('pil-upsell-row');
    if (!row) return;

    fetch(endpoint('cart.js'), { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart.items?.length) return;
        const productsInCart = {};
        cart.items.forEach((item) => { productsInCart[item.product_id] = true; });
        const productUrls = estimatedBottleCount(cart) >= 6 && caseProductUrl
          ? [caseProductUrl, ...preferredProductUrls]
          : [...preferredProductUrls];
        const lastProductId = cart.items[cart.items.length - 1].product_id;
        const recommendations = fetch(endpoint(`recommendations/products.json?product_id=${lastProductId}&limit=10&intent=related`), {
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => data.products || [])
          .catch(() => []);

        return Promise.all([Promise.all(productUrls.map(fetchProduct)), recommendations]).then(([preferred, related]) => {
          const seen = {};
          const candidates = [];
          const addCandidate = (product) => {
            const variant = product && firstVariant(product);
            if (product && !productsInCart[product.id] && !seen[product.id] && variant?.available) {
              seen[product.id] = true;
              candidates.push(product);
            }
          };
          preferred.filter(Boolean).forEach(addCandidate);
          related.forEach(addCandidate);
          if (!candidates.length) return;
          candidates.sort((left, right) => promotionRank(right) - promotionRank(left));
          row.innerHTML = candidates.slice(0, 6).map(productCard).join('');
          row.closest('[data-pil-upsell]')?.removeAttribute('hidden');
        });
      })
      .catch(() => {});
  }

  function refreshCount() {
    fetch(endpoint('cart.js'), { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const count = document.querySelector('[data-pil-cart-count]');
        if (!count) return;
        const itemCount = cart.item_count || 0;
        count.textContent = itemCount;
        count.toggleAttribute('hidden', itemCount === 0);
      })
      .catch(() => {});
  }

  function addUpsell(button) {
    if (busy) return;
    busy = true;
    button.classList.add('is-loading');
    fetch(endpoint('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number.parseInt(button.dataset.pilAdd, 10), quantity: 1 }] }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
        return response.json();
      })
      .then(() => {
        button.classList.remove('is-loading');
        button.classList.add('is-done');
        const currentPath = (window.location.pathname || '/').split('?')[0];
        return fetch(`${currentPath}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
      })
      .then((response) => response.json())
      .then((sections) => {
        const drawerHtml = sections['cart-drawer'];
        if (!drawerHtml) return;
        const source = document.createElement('div');
        source.innerHTML = drawerHtml;
        const fresh = source.querySelector('.drawer__inner');
        const current = document.querySelector('#CartDrawer .drawer__inner');
        if (fresh && current) current.replaceWith(fresh);
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.classList.remove('is-empty');
          if (!drawer.classList.contains('active')) {
            drawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }
        }
        loadUpsells();
        refreshCount();
      })
      .catch(() => { button.classList.remove('is-loading'); })
      .finally(() => { busy = false; });
  }

  function init() {
    loadUpsells();
    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-pil-add]');
      if (button) {
        event.preventDefault();
        addUpsell(button);
      }
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('#cart-icon-bubble')) setTimeout(loadUpsells, 250);
    });
    try {
      if (window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => setTimeout(() => {
          loadUpsells();
          refreshCount();
        }, 120));
      }
    } catch {}
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });
})();
